import type { OrgSettings } from '@/data/settings'
import { ImageWell } from './ImageWell'
import { SettingsCard } from './SettingsCard'

/**
 * The meeting email's mark, in the two schemes it renders in.
 *
 * One logo cannot serve both. The email goes out on white in most clients and
 * on near-black in the ones that honour the reader's dark colour scheme, and a
 * mark legible on one vanishes on the other — which nobody discovers, because
 * the person who uploaded it only ever saw their own client.
 *
 * There is no message field. The copy of this email is fixed, so the only thing
 * an organisation chooses about it is the mark at the top.
 */
export function BrandingSettings({
  draft,
  onChange,
}: {
  draft: OrgSettings
  onChange: (next: OrgSettings) => void
}) {
  const { email } = draft

  const setEmail = (patch: Partial<OrgSettings['email']>) =>
    onChange({ ...draft, email: { ...email, ...patch } })

  return (
    <SettingsCard title="Meeting email" meta="the mark only — the wording is fixed">
      <div className="space-y-4 lg:max-w-md">
        <ImageWell
          label="Black logo"
          hint="Used on the light background. Most clients."
          aspect="wide"
          surface="light"
          value={email.logoBlack}
          onChange={(value) => setEmail({ logoBlack: value })}
        />
        <ImageWell
          label="White logo"
          hint="Used when the reader's client is in dark mode."
          aspect="wide"
          surface="dark"
          value={email.logoWhite}
          onChange={(value) => setEmail({ logoWhite: value })}
        />
      </div>
    </SettingsCard>
  )
}
